import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ColorPicker } from 'primereact/colorpicker';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import type { ApplicationSdk } from '../../../core/sdk';
import DesignManagementStore from '../stores/DesignManagementStore';
import UploadImageDialog from '../../../core/dialogs/input/components/UploadImageDialog';
import './DesignManagement.css';

const LogoManagement = observer(({ store }: { store: DesignManagementStore }) => {
    const resetLogo = async () => {
        await store.resetLogo();
        store.setLogoFile(null);
    };

    const getCurrentLogoUrl = () => {
        return store.logoUrl || '/logo_maten.png';
    };

    return (
        <Card
            title="Logo Verwaltung"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="logo-management-content">
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '1rem',
                        marginBottom: '1rem',
                    }}
                >
                    <div>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Aktuelles Logo
                        </label>
                        <div
                            style={{
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '80px',
                            }}
                        >
                            <img
                                src={getCurrentLogoUrl()}
                                alt="Aktuelles Logo"
                                style={{
                                    maxHeight: '60px',
                                    maxWidth: '150px',
                                    objectFit: 'contain',
                                }}
                                onError={e => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/logo_maten.png';
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Neues Logo hochladen
                        </label>
                        <div
                            style={{
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                minHeight: '80px',
                            }}
                        >
                            <UploadImageDialog
                                store={store}
                                width={160} // Max 160px breit
                                height={40} // Immer 40px hoch
                                aspectRatio={4}
                            />
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: '0.5rem',
                        justifyContent: 'center',
                        marginTop: '1rem',
                    }}
                >
                    {store.imageBlob && (
                        <Button
                            label={store.isUploadingLogo ? 'Wird hochgeladen...' : 'Logo hochladen'}
                            icon={store.isUploadingLogo ? undefined : 'pi pi-upload'}
                            onClick={async () => await store.uploadLogo()}
                            disabled={store.isUploadingLogo}
                            className="p-button-success"
                            style={{ padding: '0.5rem 1rem' }}
                        />
                    )}
                    {store.logoUrl && (
                        <Button
                            label="Standard wiederherstellen"
                            icon="pi pi-refresh"
                            onClick={resetLogo}
                            className="p-button-outlined"
                            disabled={store.isUploadingLogo}
                            style={{ padding: '0.5rem 1rem' }}
                        />
                    )}
                    {store.isUploadingLogo && (
                        <ProgressSpinner
                            style={{ width: '20px', height: '20px' }}
                            strokeWidth="4"
                        />
                    )}
                </div>
            </div>
        </Card>
    );
});

const ColorManagement = observer(({ store }: { store: DesignManagementStore }) => {
    const [tempColor, setTempColor] = useState(store.primaryColor);

    useEffect(() => {
        setTempColor(store.primaryColor);
    }, [store.primaryColor]);

    const handleColorChange = (color: string) => {
        // Ensure the color starts with #
        const formattedColor = color.startsWith('#') ? color : `#${color}`;
        setTempColor(formattedColor);
    };

    const savePrimaryColor = async () => {
        store.setPrimaryColor(tempColor);
        await store.savePrimaryColor();
    };

    return (
        <Card
            title="Farbschema Verwaltung"
            style={{
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
            }}
        >
            <div className="color-management-content">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Aktuelle Primärfarbe
                        </label>
                        <div
                            style={{
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: store.primaryColor,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                }}
                            />
                            <span
                                style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                }}
                            >
                                {store.primaryColor}
                            </span>
                        </div>
                    </div>
                    <div>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Neue Farbe auswählen
                        </label>
                        <div
                            style={{
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                            }}
                        >
                            <ColorPicker
                                value={tempColor}
                                onChange={e => handleColorChange(e.value as string)}
                                format="hex"
                                style={{ width: '40px', height: '40px' }}
                            />
                            <InputText
                                value={tempColor}
                                onChange={e => handleColorChange(e.target.value)}
                                placeholder="#1976d2"
                                style={{ width: '120px' }}
                            />
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: '0.5rem',
                        justifyContent: 'center',
                        marginTop: '2rem',
                    }}
                >
                    <Button
                        label="Farbe speichern"
                        icon="pi pi-check"
                        onClick={savePrimaryColor}
                        className="p-button-success"
                        disabled={tempColor === store.primaryColor}
                        style={{ padding: '0.5rem 1rem' }}
                    />
                </div>
            </div>
        </Card>
    );
});

const DesignManagement = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(DesignManagementStore);
    }, [sdk]);

    const store = sdk.currentStore;

    if (!store || store.isLoading) {
        return (
            <div
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '2rem',
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ProgressSpinner style={{ display: 'block', margin: '2rem auto' }} />
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '2rem',
                minHeight: '100vh',
            }}
        >
            <div className="p-fluid">
                <LogoManagement store={store} />
                <ColorManagement store={store} />
            </div>
        </div>
    );
});

export default DesignManagement;
